/**
 * useHasRole Hook - RBAC check for protected routes
 *
 * PRIV-25.2: Implements role-based access control for Privacy KPI Dashboard
 *
 * Checks JWT claims from Auth0 profile to determine if user has required role(s).
 * Used by ProtectedRoute component to guard admin-only pages.
 *
 * @example
 * const hasAccess = useHasRole(['verascore_admin', 'auditor']);
 * if (!hasAccess) return <Unauthorized />;
 */

import { useMemo } from 'react';
import useAppState from '../common/AppState';

/**
 * Feature flag for Privacy KPI Dashboard
 * Set VITE_FEATURE_PRIVACY_KPI=true in Netlify env vars to enable
 */
export const FEATURE_PRIVACY_KPI_ENABLED = import.meta.env.VITE_FEATURE_PRIVACY_KPI === 'true';

/**
 * Temporary admin user IDs for testing before Auth0 roles are configured
 * TODO: Remove this after Auth0 roles are properly configured in production
 */
const TEMP_ADMIN_USER_IDS: string[] = [
  'auth0|698d61ef869d2aad03d5fa44', // jim.o'mara@myverascore.com
];

/**
 * Admin roles that can access the Privacy KPI Dashboard
 * These correspond to Auth0 role claims in:
 * - profile.app_metadata.roles (array)
 * - OR JWT claim: https://members.verascore.com/roles
 *
 * Accepted roles:
 * - Platform Admin: Full platform administrator (Auth0 role name)
 * - verascore_admin: Legacy admin role
 * - auditor: Compliance/audit access
 * - ops: Operations team access
 */
export const ADMIN_ROLES = ['Platform Admin', 'verascore_admin', 'auditor', 'ops'] as const;
export type AdminRole = typeof ADMIN_ROLES[number];

/**
 * Check if user has any of the required roles
 * 
 * @param requiredRoles - Array of role strings to check against
 * @returns true if user has at least one of the required roles
 */
export function useHasRole(requiredRoles: string[]): boolean {
  const { profile, orgUser } = useAppState();

  return useMemo(() => {
    // No profile = not authenticated = no access
    if (!profile) {
      console.log('[useHasRole] No profile, denying access');
      return false;
    }

    // TEMPORARY: Check if user is in the temp admin list (for testing before RBAC is configured)
    // TODO: Remove this block after Auth0 roles are properly configured
    const userId = profile.sub || profile.user_id;
    if (userId && TEMP_ADMIN_USER_IDS.includes(userId)) {
      console.log('[useHasRole] Temporary admin access granted for:', userId);
      return true;
    }

    // Check multiple sources for roles
    const roleSources: string[][] = [];

    // Source 1: app_metadata.roles (Auth0 custom claim)
    if (profile.app_metadata?.roles && Array.isArray(profile.app_metadata.roles)) {
      roleSources.push(profile.app_metadata.roles);
    }

    // Source 2: user_metadata.role (legacy single role)
    if (profile.user_metadata?.role) {
      roleSources.push([profile.user_metadata.role]);
    }

    // Source 3: orgUser.role (Supabase org_users table)
    // Map org_users roles to admin roles
    if (orgUser?.role) {
      // 'admin' in org_users maps to verascore_admin for internal staff
      // 'auditor' in org_users maps to auditor role
      const orgRoleMap: Record<string, string[]> = {
        admin: ['verascore_admin', 'admin'],
        auditor: ['auditor'],
        ops: ['ops'],
      };
      const mappedRoles = orgRoleMap[orgUser.role] || [orgUser.role];
      roleSources.push(mappedRoles);
    }

    // Flatten all role sources
    const userRoles = roleSources.flat();
    
    console.log('[useHasRole] Checking roles:', {
      requiredRoles,
      userRoles,
      sources: {
        appMetadata: profile.app_metadata?.roles,
        userMetadata: profile.user_metadata?.role,
        orgUser: orgUser?.role,
      },
    });

    // Check if any user role matches any required role
    const hasAccess = requiredRoles.some(required => 
      userRoles.some(userRole => 
        userRole.toLowerCase() === required.toLowerCase()
      )
    );

    console.log('[useHasRole] Access decision:', hasAccess);
    return hasAccess;
  }, [profile, orgUser, requiredRoles]);
}

/**
 * Check if user is a VeraScore admin (internal staff)
 */
export function useIsVeraScoreAdmin(): boolean {
  return useHasRole(['verascore_admin', 'admin']);
}

/**
 * Check if user is an auditor/regulator
 */
export function useIsAuditor(): boolean {
  return useHasRole(['auditor']);
}

/**
 * Check if user can access Privacy KPI Dashboard
 * Requires BOTH:
 * 1. Feature flag enabled (VITE_FEATURE_PRIVACY_KPI=true)
 * 2. User has admin role OR is in temp admin list
 */
export function useCanAccessPrivacyKpis(): boolean {
  const hasRole = useHasRole([...ADMIN_ROLES]);

  // Feature flag must be enabled
  if (!FEATURE_PRIVACY_KPI_ENABLED) {
    console.log('[useCanAccessPrivacyKpis] Feature flag disabled');
    return false;
  }

  return hasRole;
}

export default useHasRole;

